import React, { useState, useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import ReduxToastr from 'react-redux-toastr';
import { PersistGate } from 'redux-persist/integration/react';
import AuthenticationController from 'components/Authentication/AuthenticationController';
import LoadingScreen from 'components/Utils/LoadingScreen';
import config from 'config/config.json';
import App from 'scenes/App';
import MiscUtils from 'services/MiscUtils';
import { isPatientPortal, PORTAL } from 'services/PortalUtils';
import { store, persistor, purgeStorage } from 'careand-redux/store';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import socket from 'websockets/socket';
import configVersion from 'config/version.json';
import { disableReactDevTools } from 'services/disableReactDevTools';
import { Portal } from '@material-ui/core';
import { logout } from 'careand-redux/actions/logout';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { initializeActionExecutionWatcher } from 'services/ActionExecutionWatcher';

const PORTAL_VERSION = configVersion['client-version'];

const ErrorBoundary = MiscUtils.getBugsnagClient().getPlugin('react').createErrorBoundary(React);

const ENVIRONMENT = config.ENVIRONMENT;

if (ENVIRONMENT === 'production' && import.meta.env.PROD) disableReactDevTools();

// Initialize Proactive Carey execution watcher
initializeActionExecutionWatcher();

export const SOCKET = { instance: null };

/*const instance = createInstance({
  urlBase: "https://analytics.careand.ca",
  siteId: 1,
  trackerUrl: "https://analytics.careand.ca/rainbow",
  srcUrl: "https://analytics.careand.ca/unicorn",
  disabled: ENVIRONMENT !== 'production' || !PATIENT,
  linkTracking: true,
});*/

const theme = createTheme();

const AppWrapper = (props) => {
  const [authenticated, setAuthenticated] = useState();
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [persistLoading, setPersistLoading] = useState(true);
  const loadingStartTime = useRef(Date.now());
  const debugTimer = useRef();

  function onAuthenticationChange(authenticated) {
    setAuthenticated(authenticated);
  }

  useEffect(() => {
    if (persistLoading || authenticated === undefined) {
      // Start timer for debug panel (30 seconds, or 3 seconds in dev)
      const timeout = 15000;
      debugTimer.current = setTimeout(() => {
        // Only show for staff (non-patient) users
        if (!isPatientPortal) {
          setShowDebugPanel(true);
        }
      }, timeout);
    } else {
      // Clear timer if loading completes
      if (debugTimer.current) {
        clearTimeout(debugTimer.current);
      }
    }

    return () => {
      if (debugTimer.current) {
        clearTimeout(debugTimer.current);
      }
    };
  }, [persistLoading, authenticated]);

  const handlePersistLoad = () => {
    setPersistLoading(false);
  };

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          {showDebugPanel && (
            <DebugPanel
              loadingStartTime={loadingStartTime}
              persistLoading={persistLoading}
              authenticated={authenticated}
              onDismiss={() => setShowDebugPanel(false)}
            />
          )}
          <PersistGate
            loading={<LoadingScreen />}
            persistor={persistor}
            onBeforeLift={handlePersistLoad}
          >
            <BrowserRouter>
              <Portal>
                <ReduxToastr
                  timeOut={4000}
                  newestOnTop={false}
                  preventDuplicates
                  maxAnimationDelay={275}
                  position="top-center"
                  transitionIn="fadeIn"
                  transitionOut="fadeOut"
                  progressBar={true}
                  closeOnToastrClick={true}
                />
              </Portal>
              <AuthenticationController
                closeSocket={socket.close}
                openSocket={socket.open}
                version={PORTAL_VERSION}
                portal={PORTAL}
                onAuthenticationChange={onAuthenticationChange}
              />
              {authenticated !== undefined && (
                <App
                  authenticated={authenticated}
                  version={PORTAL_VERSION}
                  portal={PORTAL}
                  environment={ENVIRONMENT}
                />
              )}
            </BrowserRouter>
          </PersistGate>
        </Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const DebugPanel = ({ loadingStartTime, persistLoading, authenticated, onDismiss }) => {
  const dispatch = useAppDispatch();

  const forceReload = () => {
    window.location.reload();
  };

  const clearPersistedData = () => {
    dispatch(logout({ isManuallyTriggered: true }));
    purgeStorage().then(() => {
      window.location.reload();
    });
  };

  const copyDebugInfo = () => {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      loadingDuration: Date.now() - loadingStartTime.current,
      persistLoading,
      authenticated,
      userAgent: navigator.userAgent,
      url: window.location.href,
      environment: ENVIRONMENT,
      portal: PORTAL,
      version: PORTAL_VERSION,
      reduxState: store.getState() ? 'present' : 'missing',
    };

    navigator.clipboard
      .writeText(JSON.stringify(debugInfo, null, 2))
      .then(() => {
        alert('Debug info copied to clipboard');
      })
      .catch(() => {
        prompt('Copy this debug info:', JSON.stringify(debugInfo, null, 2));
      });
  };

  const BUTTON_STYLE = {
    margin: '5px',
    padding: '8px 12px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    width: 'calc(50% - 10px)',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '20px',
        borderRadius: '8px',
        zIndex: 10000,
        maxWidth: '90%',
        width: '100%',
        fontSize: '14px',
        fontFamily: 'monospace',
      }}
    >
      <h3 style={{ margin: '0 0 15px 0', color: '#ff6b6b' }}>⚠️ Loading Debug Panel</h3>
      <p>App stuck loading for {Math.floor((Date.now() - loadingStartTime.current) / 1000)}s</p>
      <p>
        <strong>Persist Loading:</strong> {persistLoading ? 'Yes' : 'No'}
      </p>
      <p>
        <strong>Authenticated:</strong> {String(authenticated)}
      </p>
      <p>
        <strong>Environment:</strong> {ENVIRONMENT}
      </p>
      <p>
        <strong>Version:</strong> {PORTAL_VERSION}
      </p>
      <div style={{ marginTop: '15px' }}>
        <button
          onClick={forceReload}
          style={{
            ...BUTTON_STYLE,
            backgroundColor: '#4CAF50',
          }}
        >
          Force Reload
        </button>
        <button
          onClick={clearPersistedData}
          style={{
            ...BUTTON_STYLE,
            backgroundColor: '#ff9800',
          }}
        >
          Clear Data & Reload
        </button>
        <button
          onClick={copyDebugInfo}
          style={{
            ...BUTTON_STYLE,
            backgroundColor: '#2196F3',
          }}
        >
          Copy Debug Info
        </button>
        <button
          onClick={onDismiss}
          style={{
            ...BUTTON_STYLE,
            backgroundColor: '#666',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default AppWrapper;
